/**
 * pastoralQuestionEngine.js
 *
 * Determines when Kevia should ask a question rather than (or in addition to) answering.
 * Pastoral questions are brief, warm, exploratory — never interrogatory.
 *
 * Question types:
 *   exploration   — open invitation to describe the situation further
 *   clarification — need more context to respond well
 *   invitation    — invite spiritual reflection at the end
 *   continuation  — invite further accompaniment, leave space open
 */

/**
 * @param {{
 *   text:   string,
 *   intent: string,
 *   emotion: object|null,
 *   timing: string,
 *   depth:  string,
 * }} params
 * @returns {{
 *   shouldQuestion: boolean,
 *   questionType:   string|null,
 *   questionHint:   string|null,
 * }}
 */
export function buildPastoralQuestion({ text, intent, emotion, timing, depth }) {
  // Never question when holding or micro — just be present
  if (timing === "hold" || depth === "micro") {
    return { shouldQuestion: false, questionType: null, questionHint: null };
  }

  // Short + accompany_first = quiet accompaniment, not exploration
  if (depth === "short" && timing === "accompany_first") {
    return { shouldQuestion: false, questionType: null, questionHint: null };
  }

  // Conflict → gentle exploration of the situation
  if (emotion?.dominantEmotion === "conflict") {
    return {
      shouldQuestion: true,
      questionType:   "exploration",
      questionHint:
        "Haz una sola pregunta suave al final que invite a describir mejor la situación, sin profundizar el conflicto.",
    };
  }

  // Unclear pastoral context → clarify before recommending
  if (
    timing === "question_first" &&
    (intent === "family_followup" || intent === "visit_plan")
  ) {
    return {
      shouldQuestion: true,
      questionType:   "clarification",
      questionHint:
        "Antes de recomendar, haz una pregunta breve y cálida para entender mejor el contexto pastoral.",
    };
  }

  // Spiritual confusion → invite reflection gently, at the end
  if (emotion?.dominantEmotion === "spiritual_confusion") {
    return {
      shouldQuestion: true,
      questionType:   "invitation",
      questionHint:
        "Al final, puedes invitar a la reflexión con una pregunta suave — no al inicio de la respuesta.",
    };
  }

  // Fear, discouragement, loneliness → leave space for continuation
  if (
    ["fear", "discouragement", "loneliness"].includes(emotion?.dominantEmotion) &&
    depth !== "short"
  ) {
    return {
      shouldQuestion: true,
      questionType:   "continuation",
      questionHint:
        "Si conviene, cierra con una pregunta suave de acompañamiento que deje espacio para continuar.",
    };
  }

  return { shouldQuestion: false, questionType: null, questionHint: null };
}
